import { useState, useCallback } from "react";
import { decodeEventLog, encodeFunctionData, TransactionReceipt } from "viem";
import { DecodedLog } from "@shared/src/lib/types";
import { WriteContractFunctionParameters } from "viem/experimental";
import { getErrorMessage } from "@shared/src/utils";
import { UserOperationCallData, waitForUserOperationTransaction } from "@aa-sdk/core";
import { useModalStore } from "@shared/src/stores/useModalStore";
import { checkIfGasSponsored } from "@shared/src/lib/functions";
import { getSingletonAccountClient, SingletonAccountClient } from "@sdk/lib/singletonAccount";
import { NetworkError } from "@sdk/errors";
import { useHostStore } from "../stores/useHostStore";
import { CopyButton } from "../components/ui/common/misc/TextWithCopy";
import { shortenAddress } from "../utils/helpers";
import { ExtendedWalletClient } from "@sdk/index";
import { IxChain } from "../config/chains";
import { useAccount, useConnectorClient } from "wagmi";

// Types
export type Options = {
  timeTimeToWait?: number;
  shouldShowModal?: boolean;
  toastId?: string;
  modalAction?: any;
  txType?: "transaction" | "contract";
  onTxSent?: (txHash: `0x${string}`) => Promise<any>;
  onReceipt?: ({ receipt, decodedLogs }: { receipt: TransactionReceipt; decodedLogs: DecodedLog[] }) => Promise<any>;
  errorMessage?: string;
  loadingMessage?: string;
  successMessage?: string;
  waitForReceipt?: boolean;
  args?: Record<string, any>;
  simulateFirst?: boolean;
  getLogs?: boolean;
  sponsorGas?: boolean;
  to?: `0x${string}`;
  value?: bigint;
  numConfirmations?: number;
};

export type WriteContractWithToastReturnType = {
  receipt?: TransactionReceipt;
  decodedLogs?: DecodedLog[];
  txHash: `0x${string}`;
};

type TransactionContext = {
  activeAddress: `0x${string}`;
  activeChain: IxChain;
  walletClient: ExtendedWalletClient;
  isEmbedded: boolean;
};

type TransactionType = "gasSponsored" | "eoa" | "sca";

// Pure functions for transaction logic
const createTransactionData = (options: Options, contractArgs?: WriteContractFunctionParameters): `0x${string}` => {
  if (options.txType === "transaction") {
    return "0x";
  }

  if (!contractArgs) {
    throw new Error("Contract arguments required for contract transactions");
  }

  return encodeFunctionData({
    abi: contractArgs.abi,
    functionName: contractArgs.functionName,
    args: contractArgs.args,
  });
};

// Transaction execution strategies
const executeEOATransaction = async (
  options: Options,
  contractArgs: WriteContractFunctionParameters | undefined,
  context: TransactionContext,
): Promise<`0x${string}`> => {
  const { walletClient, activeAddress, activeChain } = context;

  if (options.txType === "transaction") {
    return walletClient.sendTransaction({
      account: activeAddress,
      to: options.to,
      value: options.value,
      chain: activeChain,
    });
  }

  if (!contractArgs) {
    throw new Error("Contract arguments required for contract transactions");
  }

  return walletClient.writeContract({
    abi: contractArgs.abi,
    address: contractArgs.address,
    functionName: contractArgs.functionName,
    args: contractArgs.args,
    value: contractArgs.value,
    chain: activeChain,
    account: activeAddress,
  });
};

const executeSponsoredTransaction = async (
  options: Options,
  contractArgs: WriteContractFunctionParameters | undefined,
  context: TransactionContext,
  data: `0x${string}`,
): Promise<`0x${string}`> => {
  const { activeAddress, activeChain } = context;

  const singletonAccountClient = await getSingletonAccountClient(activeChain.id, activeAddress);
  if (!singletonAccountClient) {
    throw new Error("Singleton account client not found");
  }

  const uo: UserOperationCallData =
    options.txType === "transaction"
      ? {
          target: options.to as `0x${string}`,
          value: options.value,
          data,
        }
      : {
          target: contractArgs?.address as `0x${string}`,
          data,
          value: contractArgs?.value,
        };

  const op = await singletonAccountClient.sendUserOperation({
    uo,
    overrides: { nonceKey: BigInt(activeAddress) },
  });

  return op.hash;
};

const executeSCATransaction = async (
  options: Options,
  contractArgs: WriteContractFunctionParameters | undefined,
  context: TransactionContext,
  data: `0x${string}`,
): Promise<`0x${string}`> => {
  const { walletClient, activeAddress, activeChain } = context;

  const transactionParams =
    options.txType === "transaction"
      ? {
          chain: activeChain,
          to: options.to,
          value: options.value,
          account: walletClient.account || activeAddress,
        }
      : {
          chain: activeChain,
          to: contractArgs?.address,
          data,
          value: contractArgs?.value,
          account: walletClient.account || activeAddress,
        };

  return walletClient.sendTransaction(transactionParams);
};

// Simulation helper
const simulateContract = async (contractArgs: WriteContractFunctionParameters, context: TransactionContext) => {
  const { walletClient, activeAddress, activeChain } = context;

  return walletClient.simulateContract({
    abi: contractArgs.abi,
    address: contractArgs.address,
    functionName: contractArgs.functionName,
    args: contractArgs.args,
    account: activeAddress,
    chain: activeChain,
    blockTag: "latest",
  });
};

// Receipt handling
const waitForTransactionReceipt = async (
  txHash: `0x${string}`,
  context: TransactionContext,
  options: Options,
  singletonAccountClient?: SingletonAccountClient,
): Promise<TransactionReceipt | undefined> => {
  const { walletClient } = context;
  let receipt: TransactionReceipt | undefined = undefined;
  let realHash: `0x${string}` | null = null;

  try {
    const isNotUserOp = !singletonAccountClient;

    if (isNotUserOp) {
      realHash = txHash;
    } else {
      realHash = await waitForUserOperationTransaction(singletonAccountClient, { hash: txHash });
    }

    receipt = await walletClient.waitForTransactionReceipt({
      hash: realHash as `0x${string}`,
      confirmations: options.numConfirmations || 1,
      timeout: 1000 * 20,
    });
  } catch (error) {
    console.error("Error getting receipt", error);
    if (realHash) {
      receipt = await walletClient.getTransactionReceipt({ hash: realHash }).catch(() => undefined);
    }
  }

  return receipt;
};

// Log decoding
const decodeTransactionLogs = (receipt: TransactionReceipt, abi: any): DecodedLog[] => {
  const logs: DecodedLog[] = [];

  for (const log of receipt?.logs || []) {
    try {
      const decoded = decodeEventLog({
        abi,
        data: log.data,
        topics: log.topics,
      }) as DecodedLog;
      logs.push(decoded || log);
    } catch (e) {
      // Continue if log cannot be decoded
      continue;
    }
  }

  return logs;
};

// Modal management helpers

const createTransactionSentModal = (txHash: `0x${string}`, activeChain: any, loadingMessage?: string) => ({
  description: "Transaction sent Sent to the Blockchain. Waiting for confirmation.",
  message: (
    <span>
      <a href={`${activeChain?.blockExplorers?.default.url}/tx/${txHash}`} target="_blank" rel="noopener noreferrer">
        {`Tx Hash: ${shortenAddress(txHash)}`}
      </a>
      <CopyButton text={txHash} />
    </span>
  ),
});

const createSuccessModal = (options: Options) => ({
  type: "success" as const,
  title: "Transaction Successful",
  message: options?.successMessage || `${options?.args?.[0]?.functionName} successful`,
  autoClose: true,
});

const createErrorModal = (error: any, options: Options, onClose: () => void) => {
  if (error.message.includes("user rejected")) {
    return {
      type: "error" as const,
      title: "Transaction Rejected",
      message: "You rejected the transaction",
      buttonText: "Close",
      buttonClick: onClose,
    };
  }

  if (error.message.includes("unable to get receipt")) {
    return {
      type: "error" as const,
      title: "Transaction Failed",
      message: "Failed to get transaction confirmation. Please check your wallet for status.",
      buttonText: "Close",
      buttonClick: onClose,
    };
  }

  return {
    type: "error" as const,
    title: "Transaction Failed",
    message: options.errorMessage || `Transaction Failed: ${getErrorMessage(error)}`,
    buttonText: "Close",
    buttonClick: onClose,
  };
};

// Main hook
function useWriteContractWithToast() {
  const sdk = useHostStore(state => state.sdk);
  const { showModal, closeModal, updateModal } = useModalStore();
  const [isPending, setIsPending] = useState(false);
  const [options, setOptions] = useState<Options>();
  const [decodedLogs, setDecodedLogs] = useState<DecodedLog[]>([]);
  const [hash, setHash] = useState<`0x${string}`>();
  const [receipt, setReceipt] = useState<TransactionReceipt | null>();
  const connectorId = useHostStore(state => state.connectorId);

  const determineTransactionType = (
    chainId: number,
    destinationAddress: `0x${string}`,
    functionName?: string,
    sponsorGas?: boolean,
    isEmbedded?: boolean,
  ): TransactionType => {
    const sdk = useHostStore.getState().sdk;
    if (!sdk) {
      throw new Error("SDK not found, cannot determine transaction type");
    }
    const isEligibleForSponsorship = checkIfGasSponsored({
      chainId,
      destinationAddress,
      functionName,
      sdk,
      connectorId,
    });

    if (!isEligibleForSponsorship || sponsorGas === false) {
      return isEmbedded ? "sca" : "eoa";
    }

    return "gasSponsored";
  };

  // Validation helpers
  const validateContext = useCallback(() => {
    const walletClient = sdk?.auth.getWalletClient();
    if (!walletClient?.account) throw new Error("Wallet client not found");

    const activeAddress = sdk?.auth.getAddress();
    if (!activeAddress) throw new Error("No active address found");

    const activeChain = useHostStore.getState().activeChain;
    if (!activeChain?.id) throw new Error("Chain not found");

    return {
      walletClient,
      activeAddress,
      activeChain,
      isEmbedded: walletClient.account.type !== "json-rpc",
    };
  }, [sdk]);

  // Transaction execution strategy
  const executeTransaction = useCallback(
    async (
      txType: TransactionType,
      options: Options,
      contractArgs: WriteContractFunctionParameters | undefined,
      context: TransactionContext,
      data: `0x${string}`,
    ): Promise<{ transactionHash: `0x${string}`; client?: any }> => {
      let transactionHash: `0x${string}`;
      let client: any;

      switch (txType) {
        case "eoa":
          transactionHash = await executeEOATransaction(options, contractArgs, context);
          break;

        case "gasSponsored":
          transactionHash = await executeSponsoredTransaction(options, contractArgs, context, data);
          client = await getSingletonAccountClient(context.activeChain.id, context.activeAddress);
          break;

        case "sca":
          transactionHash = await executeSCATransaction(options, contractArgs, context, data);
          client = context.walletClient;
          break;

        default:
          throw new Error(`Unknown transaction type: ${txType}`);
      }

      return { transactionHash, client };
    },
    [],
  );

  // Receipt and logs processing
  const processTransactionResult = useCallback(
    async (
      txHash: `0x${string}`,
      options: Options,
      context: TransactionContext,
      singletonAccountClient?: any,
    ): Promise<{ receipt?: TransactionReceipt; decodedLogs?: DecodedLog[] }> => {
      let receipt: TransactionReceipt | undefined;
      let logs: DecodedLog[] = [];

      if (options.waitForReceipt) {
        receipt = await waitForTransactionReceipt(txHash, context, options, singletonAccountClient);
        setReceipt(receipt || null);

        if (receipt && options.getLogs && options.args?.[0]?.abi) {
          logs = decodeTransactionLogs(receipt, options.args[0].abi);
          setDecodedLogs(logs);
          await options.onReceipt?.({ receipt, decodedLogs: logs });
        }
      }

      return { receipt, decodedLogs: logs };
    },
    [],
  );

  // Error handling with simulation fallback
  const handleTransactionError = useCallback(
    async (
      error: any,
      options: Options,
      contractArgs: WriteContractFunctionParameters | undefined,
      context: TransactionContext,
    ) => {
      console.log("error in write contract with toast", { error });

      // Try simulation if not already done and it's a contract transaction
      if (!options.simulateFirst && options.txType !== "transaction" && contractArgs) {
        try {
          const simulationResult = await simulateContract(contractArgs, context);
          console.log("simulation successful after contractwrite failed", { simulationResult });
        } catch (simulationError) {
          console.error("Simulation also failed", simulationError);
          error = simulationError;
        }
      }

      const onClose = async () => {
        setIsPending(false);
        closeModal();
      };

      const errorModal = createErrorModal(error, options, onClose);
      showModal(errorModal);

      throw error;
    },
    [showModal, closeModal],
  );

  const customWriteAsync = async (
    options: Options,
    ...args: [WriteContractFunctionParameters] | []
  ): Promise<WriteContractWithToastReturnType> => {
    try {
      setIsPending(true);
      const context = validateContext();
      const contractArgs = args[0];
      // Validate destination address
      const destinationAddress = options.to || contractArgs?.address;
      if (!destinationAddress) throw new Error("No destination address found");

      // Determine transaction type and execute
      const txType = determineTransactionType(
        context.activeChain.id,
        destinationAddress,
        contractArgs?.functionName,
        options.sponsorGas,
        context.isEmbedded,
      );

      const createInitialModal = (isEmbedded: boolean) => ({
        type: "loading" as const,
        title: "Transaction In Progress",
        message: isEmbedded || txType == "gasSponsored" ? "Please wait." : "Please confirm in wallet",
        preventClose: true,
      });

      // Show initial modal
      const initialModal = createInitialModal(context.isEmbedded);
      showModal(initialModal);

      // Set options and state
      setOptions({ ...options, args: contractArgs });

      // Simulate if requested
      if (options.simulateFirst && options.txType !== "transaction" && contractArgs) {
        const simulationResult = await simulateContract(contractArgs, context);
        console.log({ simulationResult });
      }

      // Prepare transaction data
      const data = createTransactionData(options, contractArgs);

      const { transactionHash: txHash, client: singletonAccountClient } = await executeTransaction(
        txType,
        options,
        contractArgs,
        context,
        data,
      ).catch(async error => {
        await handleTransactionError(error, options, contractArgs, context);
        throw error; // Re-throw to stop execution
      });

      // Update modal with transaction hash
      showModal({
        type: "loading",
        title: "Transaction In Progress",
        message: options.loadingMessage || "Transaction sent. Waiting for confirmation.",
        preventClose: true,
      });

      // Wait before proceeding
      const timeToWait = options.timeTimeToWait || (options.onTxSent ? 0 : 5000);
      await new Promise(resolve => setTimeout(resolve, timeToWait));

      setHash(txHash);
      console.log("transaction hash", txHash);

      // Update modal with transaction link
      const transactionSentModal = createTransactionSentModal(txHash, context.activeChain, options.loadingMessage);
      updateModal(transactionSentModal);

      // Call onTxSent callback
      await options?.onTxSent?.(txHash);

      // Process receipt and logs
      const { receipt, decodedLogs } = await processTransactionResult(txHash, options, context, singletonAccountClient);

      // Show success modal
      const successModal = createSuccessModal(options);
      showModal(successModal);

      return { receipt, txHash, decodedLogs };
    } catch (error: any) {
      if (options.simulateFirst) {
        const onClose = async () => {
          setIsPending(false);
          closeModal();
        };

        showModal({
          type: "error",
          title: "Simulation Failed",
          message: error.message || "Simulation failed",
          buttonText: "Close",
          buttonClick: onClose,
        });
      }
      throw error;
    } finally {
      setIsPending(false);
    }
  };

  return {
    hash,
    receipt,
    isPending,
    writeContractAsync: customWriteAsync,
  };
}

export default useWriteContractWithToast;
